import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authAPI, deviceAPI } from "../../services/api";
import {
  clearDriverSession,
  markDeviceSession,
  updatePairedTruck,
} from "../../utils/deviceSession";
import {
  markSessionBootstrapped,
  resetSessionBootstrap,
} from "../../utils/sessionBootstrapState";
import {
  beginDeliberateLogout,
  resetSessionNotices,
} from "../../utils/sessionEvents";
import type { LoginCredentials, AuthState, User } from "../../types";

const initialState: AuthState = {
  user: getUserFromStorage(),
  token: localStorage.getItem("token"),
  loading: false,
  error: null,
};

function getUserFromStorage(): User | null {
  const userStr = localStorage.getItem("user");
  if (!userStr) return null;

  try {
    return JSON.parse(userStr) as User;
  } catch {
    return null;
  }
}

export const login = createAsyncThunk(
  "auth/login",
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    // A new session starts here — forget what the previous one latched.
    resetSessionNotices();
    try {
      const response = await authAPI.login(credentials);

      /**
       * A password login is never a truck session, even on a truck's tablet.
       *
       * Cleared before the new credentials are stored, because the marker
       * describes whichever session is in hand: a driver who signs in with their
       * email on the tablet in the cab has a paired *device* and a session with
       * no truck, and leaving a stale marker there would show them container
       * work the server refuses on every call.
       */
      clearDriverSession();

      localStorage.setItem("token", response.data.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.data.user));
      // This response is already a fresh read of the user, so the shell has
      // nothing to wait for — see useSessionBootstrap.
      markSessionBootstrapped();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  }
);

/**
 * Sign a driver in on the truck's screen with their PIN.
 *
 * A sibling of `login` rather than a branch inside it: the credential is
 * different, the endpoint is different, and what comes back carries something a
 * password login has no notion of — the truck. Folding the two together would
 * mean every caller of `login` had to start reasoning about devices.
 *
 * The token is stored under the same key as any other login, because from here
 * on it *is* an ordinary driver session — the interceptor, the refresh flow and
 * every authenticated call treat it identically. What makes it a truck session
 * is the device claim inside it and the device secret sent beside it, neither of
 * which this layer has to know about.
 */
export const pinLogin = createAsyncThunk(
  "auth/pinLogin",
  async (pin: string, { rejectWithValue }) => {
    resetSessionNotices();
    try {
      const response = await deviceAPI.pinLogin(pin);
      const { accessToken, user, truck } = response.data.data;

      localStorage.setItem("token", accessToken);
      localStorage.setItem("user", JSON.stringify(user));
      // This one *is* a truck session — the token carries a device claim and the
      // device's own record names it back. The marker is what lets the UI know
      // without decoding the token.
      markDeviceSession();
      // Signing in is the server's most authoritative statement about which
      // truck this is, so the cached copy the driver's screens read is brought
      // up to date here rather than being left at whatever pairing day recorded.
      updatePairedTruck(truck);
      /**
       * A PIN sign-in is a login, so it settles the session the same way the
       * password one does.
       *
       * This response already carries a fresh read of the user, so the shell has
       * nothing to wait for. Without it the driver would watch a `getMe` and a
       * spinner between the keypad and their truck's containers — on the screen
       * least able to afford one.
       */
      markSessionBootstrapped();

      return { token: accessToken, user, truck };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "That PIN is not recognised",
      );
    }
  },
);

/**
 * End the shift on this truck.
 *
 * Distinct from `logout` in exactly one respect, and it is the one that matters:
 * the device stays paired. Signing out is a handover between drivers, not a
 * decommissioning of the screen — clearing the pairing here would mean every
 * shift change needed an admin and a fresh code.
 *
 * The local credentials are dropped whether or not the call succeeds, for the
 * same reason `logout` does it: a driver who has tapped "End shift" has ended
 * their shift, and leaving them signed in because the network was down would be
 * the app disagreeing with the yard.
 */
export const pinLogout = createAsyncThunk(
  "auth/pinLogout",
  async (_, { rejectWithValue }) => {
    try {
      await deviceAPI.pinLogout();
      clearDriverSession();
      resetSessionBootstrap();
      return true;
    } catch (error: any) {
      clearDriverSession();
      resetSessionBootstrap();
      return rejectWithValue(error.response?.data?.message || "Sign out failed");
    }
  },
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    // Before the request, not after: the server severs this tab's socket and
    // fails its in-flight requests as part of handling it, and both of those
    // arrive looking exactly like a revocation.
    beginDeliberateLogout();
    try {
      await authAPI.logout();
      clearDriverSession();
      resetSessionBootstrap();
      return true;
    } catch (error: any) {
      clearDriverSession();
      resetSessionBootstrap();
      return rejectWithValue(error.response?.data?.message || "Logout failed");
    }
  }
);

export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.refreshToken();
      const { token } = response.data.data;
      localStorage.setItem("token", token);
      return token;
    } catch (error: any) {
      clearDriverSession();
      return rejectWithValue(
        error.response?.data?.message || "Session expired"
      );
    }
  }
);

export const getMe = createAsyncThunk(
  "auth/getMe",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.getMe();
      localStorage.setItem("user", JSON.stringify(response.data.data.user));
      return response.data.data.user;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get user"
      );
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await authAPI.forgotPassword(email);
      return response.data.message;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send reset email"
      );
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (
    {
      token,
      password,
      confirmPassword,
    }: { token: string; password: string; confirmPassword: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await authAPI.resetPassword(
        token,
        password,
        confirmPassword
      );
      return response.data.message;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reset password"
      );
    }
  }
);

export const setPassword = createAsyncThunk(
  "auth/setPassword",
  async (
    {
      token,
      password,
      confirmPassword,
    }: { token: string; password: string; confirmPassword: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await authAPI.setPassword(
        token,
        password,
        confirmPassword
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to set password"
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCredentials: (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
    },
    clearCredentials: (state) => {
      state.user = null;
      state.token = null;
      clearDriverSession();
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // PIN sign-in on a truck's screen — the same landing state as a password
      // login, because from here on it is the same kind of session.
      .addCase(pinLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(pinLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(pinLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Ending a shift clears the driver either way — see the thunk.
      .addCase(pinLogout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.error = null;
      })
      .addCase(pinLogout.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.error = null;
      })
      .addCase(logout.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.user = null;
        state.token = null;
      })
      // Get Me
      .addCase(getMe.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMe.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(getMe.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        // Last of the hand-rolled credential clears. Through the helper for the
        // same reason as the others: the truck-session marker belongs to the
        // same set of keys and must not outlive them.
        clearDriverSession();
      })
      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Set Password
      .addCase(setPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setPassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(setPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCredentials, clearCredentials } =
  authSlice.actions;
export default authSlice.reducer;
